<script setup>
import { ref } from 'vue';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

const props = defineProps({
    title: {
        type: String,
        required: true,
    },
    singular: {
        type: String,
        required: true,
    },
    icon: {
        type: Array,
        required: true,
    },
    categoryKey: {
        type: String,
        required: true,
    },
    generatePath: {
        type: String,
        required: true,
    },
    collections: {
        type: Array,
        default: () => [],
    },
    loading: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['navigate', 'create']);

const isExpanded = ref(false);

function toggleExpanded() {
    isExpanded.value = !isExpanded.value;
}

function handleCollectionClick(collection) {
    emit('navigate', { type: 'collection', id: collection.id });
}

function handleViewAll() {
    emit('navigate', { type: 'viewAllCollections', categoryKey: props.categoryKey });
}

function handleCreateCollection() {
    emit('create', props.categoryKey);
}

function handleGenerate() {
    emit('navigate', { type: 'generate', generatePath: props.generatePath });
}
</script>

<template>
    <div class="border-b border-gray-700">
        <button
            @click="toggleExpanded"
            class="w-full flex items-center justify-between px-4 py-3 text-gray-100 hover:bg-gray-700 transition-colors"
        >
            <div class="flex items-center">
                <FontAwesomeIcon :icon="icon" class="mr-3 text-gray-400 w-5" />
                <span class="font-medium">{{ title }}</span>
            </div>
            <FontAwesomeIcon
                :icon="['fas', 'chevron-right']"
                class="text-gray-400 transition-transform duration-200"
                :class="{ 'rotate-90': isExpanded }"
            />
        </button>

        <transition
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="max-h-0 opacity-0"
            enter-to-class="max-h-96 opacity-100"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="max-h-96 opacity-100"
            leave-to-class="max-h-0 opacity-0"
        >
            <div v-show="isExpanded" class="overflow-hidden bg-gray-800">
                <button
                    @click="handleGenerate"
                    class="w-full flex items-center px-4 py-2 text-sm text-blue-400 hover:bg-gray-700 hover:text-blue-300 transition-colors"
                >
                    <FontAwesomeIcon :icon="['fad', 'flask']" class="mr-3" />
                    <span>New {{ singular }}</span>
                </button>

                <div v-if="loading" class="px-4 py-3 flex items-center justify-center">
                    <FontAwesomeIcon :icon="['fas', 'spinner']" spin class="text-gray-400" />
                </div>

                <div v-else-if="collections.length === 0">
                    <button
                        @click="handleCreateCollection"
                        class="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                    >
                        <FontAwesomeIcon :icon="['fas', 'folder']" class="mr-3 text-yellow-600" />
                        <span>Your first collection</span>
                    </button>
                </div>

                <div v-else>
                    <button
                        v-for="collection in collections"
                        :key="collection.id"
                        @click="handleCollectionClick(collection)"
                        class="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                    >
                        <FontAwesomeIcon :icon="['fas', 'folder']" class="mr-3 text-yellow-600" />
                        <span class="truncate">{{ collection.title }}</span>
                        <FontAwesomeIcon :icon="['fas', 'chevron-right']" class="ml-auto text-gray-500" size="xs" />
                    </button>

                    <button
                        @click="handleViewAll"
                        class="w-full flex items-center justify-end px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition-colors"
                    >
                        <span>View all collections</span>
                        <FontAwesomeIcon :icon="['fas', 'chevron-right']" class="ml-2" size="xs" />
                    </button>
                </div>
            </div>
        </transition>
    </div>
</template>
